import React, { useState } from "react";
import { Link } from "react-router-dom";
import { usePresentation } from "@/contexts/PresentationContext";
import {
  Briefcase, CheckCircle2, XCircle, Landmark, Pickaxe, Compass, Leaf,
  Building2, Presentation, Users, Handshake, Globe, Award, ChevronRight,
  Palette, Layers, Shield, Network, Waves, TreePine, Wheat, Anchor,
  Bolt, Cable,
} from "lucide-react";

/**
 * Sovereign Platform Cockpit — Phase XII (final refinement)
 * PRESENTATION-ONLY. Consolidates:
 *   · Commercial Deployment Scenarios (Modular Sovereign Platform Framework)
 *   · Cross-Ministry Federation readiness
 *   · Presentation Profiles (Cabinet / Ministerial / Boardroom / Investor / Public)
 *   · Enterprise Design System & Regression evidence
 * Nothing here modifies backend state, business logic, or existing modules.
 */

const ACTIVE = "#C99A2E";
const RESERVED = "#5A7A8A";
const EXEC = "#7B2FBE";
const ENV = "#2E7D6B";

const MODULES = [
  { key: "Land360",         label: "Land360™",         icon: Landmark, status: "Active",   color: ACTIVE, to: "/app/land" },
  { key: "Mine360",         label: "Mine360™",         icon: Pickaxe,  status: "Active",   color: ACTIVE, to: "/app/mining" },
  { key: "Survey360",       label: "Survey360™",       icon: Compass,  status: "Active",   color: ACTIVE, to: "/app/survey" },
  { key: "Environment360",  label: "Environment360™",  icon: Leaf,     status: "Active",   color: ENV,    to: "/app/environment" },
  { key: "GSROS",           label: "GSROS™",           icon: Building2,status: "Active",   color: EXEC,   to: "/app/gsros" },
  { key: "Water360",        label: "Water360™",        icon: Waves,    status: "Reserved", color: RESERVED, to: "/app/thom360" },
  { key: "Forestry360",     label: "Forestry360™",     icon: TreePine, status: "Reserved", color: RESERVED, to: "/app/thom360" },
  { key: "Agriculture360",  label: "Agriculture360™",  icon: Wheat,    status: "Reserved", color: RESERVED, to: "/app/thom360" },
  { key: "Marine360",       label: "Marine360™",       icon: Anchor,   status: "Reserved", color: RESERVED, to: "/app/thom360" },
  { key: "Energy360",       label: "Energy360™",       icon: Bolt,     status: "Reserved", color: RESERVED, to: "/app/thom360" },
  { key: "Infrastructure360", label: "Infrastructure360™", icon: Cable, status: "Reserved", color: RESERVED, to: "/app/thom360" },
];

// Independent deployment scenarios — federation matrix
const SCENARIOS = [
  { code: "A", name: "Ministry of Mines only",              activates: ["Mine360"] },
  { code: "B", name: "Add Ministry of Lands later",         activates: ["Land360"] },
  { code: "C", name: "Environment joins after three years", activates: ["Environment360"] },
  { code: "D", name: "Cabinet purchases GSROS",             activates: ["GSROS"] },
  { code: "E", name: "Full national deployment",            activates: ["Land360", "Mine360", "Survey360", "Environment360", "GSROS"] },
];

// Presentation Profiles
const PROFILES = [
  { key: "cabinet",       label: "Cabinet Presentation",           icon: Building2,  accent: EXEC,   audience: "Cabinet of Ministers" },
  { key: "ministerial",   label: "Ministerial Presentation",       icon: Award,      accent: ACTIVE, audience: "Honourable Minister" },
  { key: "ps",            label: "Permanent Secretary",             icon: Briefcase,  accent: ACTIVE, audience: "Permanent Secretary" },
  { key: "parliament",    label: "Parliamentary Committee",        icon: Users,      accent: EXEC,   audience: "Portfolio Committee" },
  { key: "boardroom",     label: "Boardroom Mode",                 icon: Presentation,accent: EXEC,  audience: "Command centre screens", to: "/app/boardroom" },
  { key: "investor",      label: "Investor Demonstration",         icon: Handshake,  accent: "#6BB88A", audience: "International investor" },
  { key: "partner",       label: "Development Partner",            icon: Globe,      accent: "#4FA3D1", audience: "WB · IMF · AU · SADC · IFC · OECD" },
  { key: "public",        label: "Public Transparency",            icon: Globe,      accent: "#6BB88A", audience: "Citizens & press" },
];

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Check = ({ ok = true, children }) => (
  <div className="border-b border-white/5 pb-1 flex items-start gap-2 py-1">
    {ok ? <CheckCircle2 size={12} className="text-[#6BB88A] mt-0.5" /> : <XCircle size={12} className="text-[#E67E7E] mt-0.5" />}
    <div className="text-[12px] text-[#F6F8FA]">{children}</div>
  </div>
);

export const SovereignCockpit = () => {
  const { presentation, setPresentation, role, setRole } = usePresentation();
  const [scenario, setScenario] = useState("E");
  const active = SCENARIOS.find((s) => s.code === scenario) || SCENARIOS[4];
  const activeSet = new Set(active.activates);

  return (
    <div className="p-6 space-y-4" data-testid="page-sovereign-cockpit">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: EXEC + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${EXEC}25 100%)` }}>
        <Briefcase size={38} className="text-[#C99A2E] mt-1" />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#C99A2E]">
            Cadastre360™ · Phase XII · Sovereign Platform Cockpit v1.12
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            Enterprise refinement · commercial deployment · executive presentation
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl">
            THOM360 Modular Sovereign Platform Framework, federation readiness, presentation profiles
            and design-system evidence — all presentation-only. Zero regression, zero duplication.
          </div>
        </div>
      </div>

      {/* Commercial Deployment Scenarios */}
      <Card title="Commercial deployment scenarios" accent={ACTIVE} testId="cockpit-scenarios">
        <div className="flex flex-wrap gap-1 mb-3">
          {SCENARIOS.map((s) => (
            <button key={s.code} onClick={() => setScenario(s.code)}
                    data-testid={"scenario-" + s.code}
                    className="rounded-sm px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.14em] border"
                    style={{
                      background: scenario === s.code ? ACTIVE + "30" : "transparent",
                      color: scenario === s.code ? "#F6F8FA" : "#B3C2CC",
                      borderColor: scenario === s.code ? ACTIVE : "rgba(255,255,255,0.08)",
                    }}>
              Scenario {s.code} · {s.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {MODULES.filter((m) => m.status === "Active").map((m) => {
            const activated = activeSet.has(m.key);
            return (
              <div key={m.key} className="rounded-sm border p-2.5 flex items-start gap-2"
                   style={{ borderColor: m.color + "40",
                             background: activated ? "rgba(7,27,43,0.55)" : "rgba(90,122,138,0.08)",
                             opacity: activated ? 1 : 0.5 }}>
                <m.icon size={18} style={{ color: m.color }} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-bold text-[#F6F8FA] text-[13.5px]">{m.label}</div>
                    <span className="ml-auto rounded-sm px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em]"
                          style={{ background: (activated ? "#6BB88A" : "#7C93A5") + "25",
                                   color: activated ? "#6BB88A" : "#7C93A5" }}>
                      {activated ? "Provisioned" : "Not procured"}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#DCE3E8] mt-1">
                    Federates with all THOM360 modules regardless of activation order.
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-2 text-[11px] text-[#7C93A5] italic">
          Deactivated modules never produce errors — federation resolves service availability dynamically.
        </div>
      </Card>

      {/* Presentation Profiles */}
      <Card title="Executive presentation profiles" accent={EXEC} testId="cockpit-profiles">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button onClick={() => setPresentation(!presentation)}
                  data-testid="cockpit-toggle-presentation"
                  className="rounded-sm px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.14em] border inline-flex items-center gap-1"
                  style={{
                    background: presentation ? EXEC + "30" : "transparent",
                    borderColor: presentation ? EXEC : "rgba(255,255,255,0.1)",
                    color: presentation ? "#F6F8FA" : "#B3C2CC",
                  }}>
            <Presentation size={11} /> Presentation Mode {presentation ? "ON" : "OFF"}
          </button>
          <span className="font-mono text-[10px] text-[#7C93A5] uppercase tracking-[0.14em]">
            Active role: <span className="text-[#F6F8FA]">{role.replace(/_/g, " ")}</span>
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          {PROFILES.map((p) => (
            <div key={p.key} className="rounded-sm border p-2.5"
                 style={{ borderColor: p.accent + "40", background: "rgba(7,27,43,0.5)" }}
                 data-testid={"profile-" + p.key}>
              <p.icon size={16} style={{ color: p.accent }} />
              <div className="font-display font-bold text-[#F6F8FA] text-[13px] mt-1">{p.label}</div>
              <div className="text-[10.5px] font-mono text-[#7C93A5] uppercase tracking-[0.14em] mt-0.5">
                for {p.audience}
              </div>
              {p.to && (
                <Link to={p.to} className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase text-[#B3C2CC]">
                  Launch <ChevronRight size={9} />
                </Link>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Federation & Design System evidence */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Cross-Ministry federation readiness" accent="#4FA3D1" testId="cockpit-federation">
          <Check>Each ministry may own users, branding, workflows, releases and configuration.</Check>
          <Check>Federation is optional — never mandatory. Ministries may deploy independently.</Check>
          <Check>Missing modules never break the platform — dynamic service discovery in place.</Check>
          <Check>Existing authentication, RBAC and audit remain unchanged.</Check>
        </Card>
        <Card title="THOM Sovereign Design System" accent="#C99A2E" testId="cockpit-design-system">
          <Check>Typography · colours · icons · spacing · dark theme governed centrally.</Check>
          <Check>Semantic colour hierarchy (Government · Strategic · Operational · Warning · Critical).</Check>
          <Check>All new pillars comply with existing card, table, dialog and chart standards.</Check>
          <Check>Executive Command Palette (Cmd/Ctrl+K) available on every authenticated page.</Check>
          <Check>Cross-domain accessibility & responsive behaviour preserved.</Check>
        </Card>
      </div>

      {/* Regression evidence */}
      <Card title="Zero-regression evidence" accent="#6BB88A" testId="cockpit-regression">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
          <Check>Land, Mining, Survey and Environmental Governance modules unchanged.</Check>
          <Check>GSROS orchestration layer unchanged.</Check>
          <Check>Sovereign Intelligence Centre unchanged.</Check>
          <Check>Executive Attention Room, Watchlist, Timeline, Boardroom operational.</Check>
          <Check>All existing routes remain unchanged — Phase XII only adds new routes.</Check>
          <Check>All existing APIs remain unchanged — no endpoint renamed or removed.</Check>
          <Check>Authentication, JWT and RBAC unchanged.</Check>
          <Check>Digital Twin, GIS, AI, Notifications, Reporting engines unchanged.</Check>
        </div>
      </Card>

      <div className="rounded-sm border border-white/10 p-3 flex flex-wrap items-center gap-3 text-[11px] text-[#7C93A5] font-mono uppercase tracking-[0.16em]"
           style={{ background: "rgba(11,53,88,0.2)" }}>
        <Network size={12} className="text-[#C99A2E]" />
        <span>THOM360 Enterprise Platform</span>
        <span>·</span>
        <span>Modular Sovereign Framework</span>
        <span>·</span>
        <span>Federation-Ready</span>
        <Palette size={12} className="ml-auto text-[#C99A2E]" />
        <span>Sovereign Design System</span>
        <Shield size={12} className="text-[#6BB88A]" />
        <span>Zero Regression</span>
      </div>
    </div>
  );
};

export default SovereignCockpit;
